/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import upperFirst from 'lodash/upperFirst';

import { borderRadius, gridSize } from '@atlaskit/theme/constants';

import type { PaletteGroup } from '../grouped-tokens';
import { getTextColorForBackground, hexToRGBA } from '../color-detection';
import { listItemStyleReset, listStyleReset } from '../index';

import CopyButton from './copy-button';

const subgroupsListStyles = css({
  display: 'flex',
  flexWrap: 'wrap',
  marginLeft: `-${gridSize() / 2}px`,
  marginRight: `-${gridSize() / 2}px`,
});

const subgroupStyles = css({
  flex: '1 1 auto',
  minWidth: '50%',
  paddingLeft: gridSize() / 2,
  paddingRight: gridSize() / 2,
  marginBottom: gridSize() * 3,

  '@media (min-width: 1241px)': {
    minWidth: '25%',
  },
});

const subgroupTitleStyles = css({
  // Override c11n specificity issue
  'p&': {
    marginTop: gridSize() * 3,
    marginBottom: gridSize() * 2,
    fontWeight: 'bold',
    lineHeight: 1,
  },
});

const copyButtonLightStyles = css({
  '&:hover, &:focus': {
    // Must not use Design Tokens as it casts onto a hardcoded surface
    // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
    background: 'rgba(0, 0, 0, 0.1)',
  },

  '&:active': {
    // Must not use Design Tokens as it casts onto a hardcoded surface
    // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
    background: 'rgba(0, 0, 0, 0.2)',
  },
});

const copyButtonDarkStyles = css({
  '&:hover, &:focus': {
    // Must not use Design Tokens as it casts onto a hardcoded surface
    // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
    background: 'rgba(255, 255, 255, 0.1)',
  },

  '&:active': {
    // Must not use Design Tokens as it casts onto a hardcoded surface
    // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
    background: 'rgba(255, 255, 255, 0.2)',
  },
});

const copyButtonTheme = {
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
  white: copyButtonDarkStyles,
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
  black: copyButtonLightStyles,
};

const SubGroup = (group: PaletteGroup) => (
  <div
    css={[
      {
        marginTop: gridSize() * 2,
      },
      group.name.includes('neutral') && {
        paddingLeft: gridSize() * 3,
        paddingRight: gridSize() * 3,
        borderRadius: borderRadius(),
      },
      group.name === 'light neutrals' && {
        // Must not use Design Tokens as the alpha tokens need to cast onto this
        // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
        backgroundColor: '#FAFBFC',
      },
      group.name === 'dark neutrals' && {
        // Must not use Design Tokens as the alpha tokens need to cast onto this
        // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
        backgroundColor: '#161A1D',
      },
    ]}
  >
    <ul css={[listStyleReset, subgroupsListStyles]}>
      {group.subgroups.map((subgroup) => (
        <li key={subgroup.name} css={[listItemStyleReset, subgroupStyles]}>
          <p
            css={[
              subgroupTitleStyles,
              group.name === 'light neutrals' &&
                css({
                  // Must not use Design Tokens as it casts onto a hardcoded surface
                  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
                  color: '#172B4D',
                }),
              group.name === 'dark neutrals' &&
                css({
                  // Must not use Design Tokens as it casts onto a hardcoded surface
                  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
                  color: '#C7D1DB',
                }),
            ]}
          >
            {upperFirst(subgroup.name)}
          </p>
          {subgroup.tokens.length > 0 ? (
            <ul
              css={[
                listStyleReset,
                {
                  borderRadius: borderRadius(),
                  overflow: 'hidden',
                },
              ]}
            >
              {subgroup.tokens.map((token) => {
                const tokenName = token.path[token.path.length - 1];

                const color = getTextColorForBackground(token.value, {
                  hardcodedSurface: group.name.includes('neutral')
                    ? (group.name.replace(' neutrals', '') as 'light' | 'dark')
                    : undefined,
                });

                const copyButtonStyles = copyButtonTheme[color];

                return (
                  <li
                    key={token.value}
                    css={css([
                      listItemStyleReset,
                      {
                        backgroundColor: token.value,
                        color,
                        padding: `${gridSize()}px ${gridSize() * 2}px`,
                        display: 'flex',
                        justifyContent: 'space-between',

                        '&:hover .token-alt-values, &:focus-within .token-alt-values': {
                          opacity: 1,
                        },
                      },
                    ])}
                  >
                    <CopyButton
                      value={tokenName}
                      css={[
                        {
                          marginLeft: '-6px',
                        },
                        copyButtonStyles,
                      ]}
                    >
                      {tokenName}
                    </CopyButton>
                    <div
                      className="token-alt-values"
                      css={css({
                        marginRight: '-6px',
                        display: 'flex',
                        opacity: 0,
                      })}
                    >
                      <CopyButton value={token.value} css={copyButtonStyles}>
                        Hex
                      </CopyButton>
                      <CopyButton
                        value={hexToRGBA(token.value)}
                        css={copyButtonStyles}
                      >
                        RGB
                      </CopyButton>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </li>
      ))}
    </ul>
  </div>
);

export default SubGroup;
